/**
 * Клітинки посуточної сітки табеля (режим «По днях періоду»).
 *
 * ч.1207 (рішення власника): на робочому дні замість прочерка має стояти «0» —
 * робочий день за графіком, на якому годин не відпрацьовано. Це окремий стан,
 * не вихідний: код `W` з `hours = 0`. Бухгалтеру потрібна можливість нульового
 * табеля (усі робочі дні по 0 годин → 0 днів, 0 годин у нарахуванні).
 *
 * Відпрацьований день = робочий день з годинами > 0. Робочий день з 0 годин у
 * дні не рахується (інакше оклад нарахувався б за «дні» без годин), але
 * лишається в сітці як «0», а не як прочерк.
 */

export type TimesheetDayCode = 'W' | 'V' | 'S' | 'A' | 'OFF';

export interface TimesheetDayCell {
  day: number;
  is_work: boolean;
  hours: number;
  code?: TimesheetDayCode;
}

export interface TimesheetDaySummary {
  working_days: number;
  worked_hours: number;
  vacation_days: number;
  sick_days: number;
  absence_days: number;
}

/** Робочий день за графіком (у т.ч. з 0 годин). Легасі-сітка без code — за is_work. */
export function isWorkDay(d: Pick<TimesheetDayCell, 'is_work' | 'code'>): boolean {
  return d.code === 'W' || (d.is_work && !d.code);
}

/** Відпрацьований день: робочий і з годинами > 0. */
export function isWorkedDay(d: TimesheetDayCell): boolean {
  return isWorkDay(d) && Number(d.hours) > 0;
}

/** Текст у клітинці → стан дня.
 *  «В» відпустка, «Х»/«Л» лікарняний, «П» прогул, число ≥ 0 — робочий день з
 *  такою кількістю годин («0» — робочий день без годин), порожньо — вихідний. */
export function parseDayCell(raw: string): { code: TimesheetDayCode; is_work: boolean; hours: number } {
  const t = raw.trim().toUpperCase();
  if (t === '') return { code: 'OFF', is_work: false, hours: 0 };
  if (t === 'В' || t === 'V') return { code: 'V', is_work: false, hours: 0 };
  if (t === 'Х' || t === 'X' || t === 'H' || t === 'Л' || t === 'L')
    return { code: 'S', is_work: false, hours: 0 };
  if (t === 'П' || t === 'P') return { code: 'A', is_work: false, hours: 0 };
  const num = Number(t.replace(',', '.'));
  if (Number.isFinite(num) && num >= 0) return { code: 'W', is_work: true, hours: num };
  return { code: 'OFF', is_work: false, hours: 0 };
}

/** Підпис клітинки: В/Л/П, години робочого дня («0» — без годин), порожньо — вихідний. */
export function dayCellText(d?: TimesheetDayCell): string {
  if (!d) return '';
  if (d.code === 'V') return 'В';
  if (d.code === 'S') return 'Л';
  if (d.code === 'A') return 'П';
  if (isWorkDay(d)) return String(Number(d.hours) || 0);
  return '';
}

/** Підсумки рядка з сітки: дні (лише з годинами > 0), години, В/Л/П. */
export function summarizeDays(days: TimesheetDayCell[]): TimesheetDaySummary {
  let working_days = 0;
  let worked_hours = 0;
  let vacation_days = 0;
  let sick_days = 0;
  let absence_days = 0;
  for (const d of days) {
    if (isWorkDay(d)) {
      const h = Number(d.hours) || 0;
      if (h > 0) working_days += 1;
      worked_hours += h;
    } else if (d.code === 'V') vacation_days += 1;
    else if (d.code === 'S') sick_days += 1;
    else if (d.code === 'A') absence_days += 1;
  }
  return {
    working_days, worked_hours: Math.round(worked_hours * 100) / 100,
    vacation_days, sick_days, absence_days,
  };
}

/** Нульовий табель: усі робочі дні → 0 годин (В/Л/П і вихідні не чіпаємо). */
export function zeroWorkDays(days: TimesheetDayCell[]): TimesheetDayCell[] {
  return days.map((d) => (isWorkDay(d) ? { ...d, code: 'W' as const, is_work: true, hours: 0 } : d));
}
